// C:\IntegraNube_WebStore\src\components\SeoClient.js
'use strict';

import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/api';

/* ===================================================================== */
/* Helpers                                                               */
/* ===================================================================== */

function cleanString(value) {
  const text = String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return text || '';
}

function truncateText(value, maxLength) {
  const text = cleanString(value);

  if (!text || text.length <= maxLength) {
    return text;
  }

  const candidate = text.slice(0, Math.max(1, maxLength - 1));
  const lastSpace = candidate.lastIndexOf(' ');
  const trimmed =
    lastSpace >= Math.floor(maxLength * 0.65)
      ? candidate.slice(0, lastSpace)
      : candidate;

  return `${trimmed.trim()}…`;
}

function hasToken(text, token) {
  const a = cleanString(text).toLowerCase();
  const b = cleanString(token).toLowerCase();
  return Boolean(a && b && a.includes(b));
}

function storeName(tienda) {
  return (
    cleanString(tienda?.nombre) ||
    cleanString(tienda?.nombreLegal) ||
    'Tienda Online'
  );
}

function storeLegalName(tienda) {
  return cleanString(tienda?.nombreLegal);
}

function storeLogo(tienda) {
  return (
    cleanString(tienda?.branding?.logo) ||
    cleanString(tienda?.branding?.favicon) ||
    ''
  );
}

function storeFavicon(tienda) {
  return cleanString(tienda?.branding?.favicon);
}

function storeOgImage(tienda) {
  const source =
    cleanString(tienda?.branding?.logo) ||
    cleanString(tienda?.branding?.favicon);

  if (!source) return '';

  try {
    const url = new URL(source);
    const match = url.pathname.match(/^(.*\/imagenes\/[^/]+)\/[^/]+$/i);

    if (!match) return '';

    url.pathname = `${match[1]}/og-image.jpg`;
    url.hash = '';

    return url.toString();
  } catch (_) {
    return '';
  }
}

function canonicalBase(storeMeta) {
  const domain = cleanString(
    storeMeta?.primaryDomain ||
    storeMeta?.domain
  );

  if (domain) {
    return `https://${domain}`;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return '';
}

function normalizeImages(product) {
  const values = [];

  if (Array.isArray(product?.imagenes)) {
    values.push(...product.imagenes);
  }

  if (product?.imagen) {
    values.unshift(product.imagen);
  }

  return [
    ...new Set(
      values
        .map((item) => {
          if (typeof item === 'string') {
            return cleanString(item);
          }

          if (item && typeof item === 'object') {
            return cleanString(
              item.url ||
              item.url_publica ||
              item.src ||
              item.imagen
            );
          }

          return '';
        })
        .filter(Boolean)
    ),
  ];
}

function productTitle(product, tienda) {
  const name = cleanString(product?.nombre) || 'Producto';
  const sku = cleanString(product?.sku);
  const nameSuffix =
    sku && !hasToken(name, sku)
      ? ` | ${sku} | ${storeName(tienda)}`
      : ` | ${storeName(tienda)}`;

  const maxLength = 75;
  const maxNameLength = Math.max(20, maxLength - nameSuffix.length);
  const compactName = truncateText(name, maxNameLength);

  return `${compactName}${nameSuffix}`;
}

function formatBrandForDescription(value) {
  const brand = cleanString(value);
  if (!brand) return '';

  const originalMatch = brand.match(/^ORIGINAL\s+(.+)$/i);

  if (originalMatch) {
    const normalizedBrand = originalMatch[1]
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return normalizedBrand
      ? `original ${normalizedBrand}`
      : 'original';
  }

  return brand;
}

function productDescription(product, tienda) {
  const name = cleanString(product?.nombre) || 'Producto';
  const sku = cleanString(product?.sku);
  const brand = cleanString(product?.marca?.nombre);
  const brandText = formatBrandForDescription(brand);
  const nameStore = storeName(tienda);

  let productText = name;

  if (
    brandText &&
    !hasToken(name, brand) &&
    !hasToken(name, brandText)
  ) {
    productText += ` ${brandText}`;
  }

  const parts = [productText];

  if (sku) {
    parts.push(`SKU ${sku}`);
  }

  parts.push(`Consulte precio y disponibilidad en ${nameStore}`);

  return truncateText(
    `${parts
      .map((part) => String(part).replace(/[.]+$/, ''))
      .join('. ')}.`,
    165
  );
}

function organizationJsonLd(base, tienda) {
  const name = storeName(tienda);
  const legalName = storeLegalName(tienda);
  const result = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${base}/#organization`,
    name,
    url: `${base}/`,
  };

  if (legalName && legalName !== name) {
    result.legalName = legalName;
  }

  const logo = storeLogo(tienda);
  if (logo) result.logo = logo;

  const email = cleanString(tienda?.contacto?.email);
  const phone = cleanString(
    tienda?.contacto?.telefono ||
    tienda?.contacto?.whatsapp
  );

  if (email) result.email = email;
  if (phone) result.telephone = phone;

  const addressParts = {
    streetAddress: cleanString(tienda?.direccion?.direccion),
    addressLocality: cleanString(
      tienda?.direccion?.comuna ||
      tienda?.direccion?.ciudad
    ),
    addressRegion: cleanString(tienda?.direccion?.region),
    addressCountry: 'CL',
  };

  if (
    addressParts.streetAddress ||
    addressParts.addressLocality ||
    addressParts.addressRegion
  ) {
    result.address = {
      '@type': 'PostalAddress',
      ...Object.fromEntries(
        Object.entries(addressParts).filter(([, value]) => Boolean(value))
      ),
    };
  }

  const sameAs = [
    tienda?.redes?.instagram,
    tienda?.redes?.facebook,
    tienda?.redes?.tiktok,
    tienda?.integraciones?.mercadolibre?.activa
      ? tienda?.integraciones?.mercadolibre?.urlTienda
      : null,
  ]
    .map(cleanString)
    .filter(Boolean);

  if (sameAs.length) {
    result.sameAs = [...new Set(sameAs)];
  }

  return result;
}

function websiteJsonLd(base, tienda) {
  const name = storeName(tienda);
  const legalName = storeLegalName(tienda);
  const result = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${base}/#website`,
    url: `${base}/`,
    name,
    publisher: {
      '@id': `${base}/#organization`,
    },
    inLanguage: cleanString(tienda?.regionalizacion?.locale) || 'es-CL',
  };

  if (legalName && legalName !== name) {
    result.alternateName = legalName;
  }

  return result;
}

function breadcrumbJsonLd(base, canonical, product) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: `${base}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Productos',
        item: `${base}/productos`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: cleanString(product?.nombre) || 'Producto',
        item: canonical,
      },
    ],
  };
}

function productJsonLd(base, canonical, product, tienda, description) {
  const result = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${canonical}#product`,
    name: cleanString(product?.nombre) || 'Producto',
    url: canonical,
    description,
    mainEntityOfPage: canonical,
  };

  const sku = cleanString(product?.sku);
  if (sku) result.sku = sku;

  const brand = cleanString(product?.marca?.nombre);
  if (brand) {
    result.brand = {
      '@type': 'Brand',
      name: brand,
    };
  }

  /*
   * Product.image se declara sólo con imágenes reales del producto.
   * NO usamos og-image.jpg/logo como foto artificial del producto.
   */
  const images = normalizeImages(product);
  if (images.length) {
    result.image = images;
  }

  const categories = Array.isArray(product?.categorias)
    ? product.categorias
        .map((item) => cleanString(item?.nombre))
        .filter(Boolean)
    : [];

  if (categories.length) {
    result.category = categories;
  }

  const price = Number(product?.precio);
  const currency =
    cleanString(
      product?.moneda ||
      tienda?.regionalizacion?.moneda
    ) || 'CLP';

  if (Number.isFinite(price) && price > 0) {
    result.offers = {
      '@type': 'Offer',
      url: canonical,
      priceCurrency: currency,
      price: Number(price.toFixed(2)),
      availability:
        product?.disponible === false
          ? 'https://schema.org/OutOfStock'
          : 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@id': `${base}/#organization`,
      },
    };
  }

  return result;
}

/* ===================================================================== */
/* Rutas                                                                 */
/* ===================================================================== */

const NOINDEX_PATHS = new Set([
  '/carrito',
  '/finalizar-compra',
  '/checkout',
  '/seguimiento-pedido',
  '/pedido-exitoso',
  '/resultado-pago',
]);

const INDEXABLE_PAGE_META = {
  '/contacto': {
    label: 'Contacto',
    description: (name) =>
      `Información de contacto de ${name}. Consulte canales de atención, dirección y medios disponibles.`,
  },
  '/despachos': {
    label: 'Despachos y retiros',
    description: (name) =>
      `Consulte opciones de despacho y retiro disponibles en ${name}.`,
  },
  '/preguntas-frecuentes': {
    label: 'Preguntas frecuentes',
    description: (name) =>
      `Respuestas a preguntas frecuentes sobre compras, productos y atención en ${name}.`,
  },
  '/cambios-devoluciones': {
    label: 'Cambios y devoluciones',
    description: (name) =>
      `Consulte las condiciones de cambios y devoluciones de ${name}.`,
  },
  '/ofertas': {
    label: 'Ofertas',
    description: (name) =>
      `Revise productos y ofertas disponibles en ${name}.`,
  },
};

function decodePathIdentifier(value) {
  try {
    return cleanString(decodeURIComponent(value));
  } catch (_) {
    return '';
  }
}

function canonicalProductIdentifier(pathname) {
  const match = String(pathname || '').match(/^\/producto\/([^/]+)\/?$/i);
  return match ? decodePathIdentifier(match[1]) || null : null;
}

function buildCanonicalProductUrl(base, slug) {
  const normalized = cleanString(slug);
  return normalized
    ? `${base}/producto/${encodeURIComponent(normalized)}`
    : `${base}/productos`;
}

/* ===================================================================== */
/* Estado SEO                                                            */
/* ===================================================================== */

function buildState({
  pathname,
  search,
  tienda,
  storeMeta,
  product,
}) {
  if (!tienda) return null;

  const base = canonicalBase(storeMeta);
  if (!base) return null;

  const name = storeName(tienda);
  const commonJsonLd = [
    organizationJsonLd(base, tienda),
    websiteJsonLd(base, tienda),
  ];
  const image = storeOgImage(tienda);
  const favicon = storeFavicon(tienda);

  if (pathname === '/') {
    return {
      route: 'home',
      path: '/',
      title:
        cleanString(tienda?.seo?.titulo) ||
        `${name} | Tienda Online`,
      description: truncateText(
        cleanString(tienda?.seo?.descripcion) ||
        `Encuentre productos en ${name}.`,
        165
      ),
      canonical: `${base}/`,
      robots:
        'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      type: 'website',
      image,
      favicon,
      jsonLd: commonJsonLd,
    };
  }

  if (pathname === '/productos') {
    const filtered = Boolean(search);

    return {
      route: 'products',
      path: `${pathname}${search || ''}`,
      title: truncateText(`Productos | ${name}`, 75),
      description: truncateText(
        `Explore el catálogo de productos de ${name}. Consulte precios, disponibilidad, marcas y alternativas disponibles.`,
        165
      ),
      canonical: `${base}/productos`,
      robots: filtered
        ? 'noindex,follow'
        : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      type: 'website',
      image,
      favicon,
      jsonLd: commonJsonLd,
    };
  }

  if (NOINDEX_PATHS.has(pathname)) {
    const labels = {
      '/carrito': 'Carrito',
      '/finalizar-compra': 'Finalizar compra',
      '/checkout': 'Finalizar compra',
      '/seguimiento-pedido': 'Seguimiento de pedido',
      '/pedido-exitoso': 'Pedido recibido',
      '/resultado-pago': 'Resultado de pago',
    };

    const label = labels[pathname] || 'Página';

    return {
      route: 'transactional',
      path: `${pathname}${search || ''}`,
      title: `${label} | ${name}`,
      description: `${label} en ${name}.`,
      canonical: `${base}${pathname}`,
      robots: 'noindex,follow',
      type: 'website',
      image,
      favicon,
      jsonLd: commonJsonLd,
    };
  }

  const pageMeta = INDEXABLE_PAGE_META[pathname];
  if (pageMeta) {
    return {
      route: 'page',
      path: pathname,
      title: truncateText(`${pageMeta.label} | ${name}`, 75),
      description: truncateText(pageMeta.description(name), 165),
      canonical: `${base}${pathname}`,
      robots:
        'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      type: 'website',
      image,
      favicon,
      jsonLd: commonJsonLd,
    };
  }

  const identifier = canonicalProductIdentifier(pathname);

  if (identifier && product) {
    const slug = cleanString(product?.slug) || identifier;
    const canonical = buildCanonicalProductUrl(base, slug);
    const description = productDescription(product, tienda);
    const images = normalizeImages(product);
    const price = Number(product?.precio);

    return {
      route: 'product',
      path: pathname,
      title: productTitle(product, tienda),
      description,
      canonical,
      robots:
        'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      type: 'product',
      image: images[0] || image,
      favicon,
      price: Number.isFinite(price) ? price : null,
      currency:
        cleanString(
          product?.moneda ||
          tienda?.regionalizacion?.moneda
        ) || 'CLP',
      jsonLd: [
        ...commonJsonLd,
        productJsonLd(
          base,
          canonical,
          product,
          tienda,
          description
        ),
        breadcrumbJsonLd(base, canonical, product),
      ],
    };
  }

  return null;
}

/* ===================================================================== */
/* DOM                                                                   */
/* ===================================================================== */

function removeManagedSeoNodes() {
  if (typeof document === 'undefined') return;

  document
    .querySelectorAll(
      '[data-integranube-seo="true"], [data-integranube-seo-client="true"]'
    )
    .forEach((node) => node.remove());
}

function addMeta(attributes) {
  const node = document.createElement('meta');

  Object.entries(attributes).forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== ''
    ) {
      node.setAttribute(key, String(value));
    }
  });

  node.setAttribute('data-integranube-seo-client', 'true');
  document.head.appendChild(node);
  return node;
}

function addLink(attributes) {
  const node = document.createElement('link');

  Object.entries(attributes).forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== ''
    ) {
      node.setAttribute(key, String(value));
    }
  });

  node.setAttribute('data-integranube-seo-client', 'true');
  document.head.appendChild(node);
  return node;
}

function addJsonLd(value) {
  const node = document.createElement('script');
  node.type = 'application/ld+json';
  node.textContent = JSON.stringify(value);
  node.setAttribute('data-integranube-seo-client', 'true');
  document.head.appendChild(node);
}

function applyState(state) {
  if (!state || typeof document === 'undefined') {
    return;
  }

  /*
   * Clave de la corrección:
   * el HTML inicial ya trae SEO SSR desde _middleware.js. Al hidratar React
   * no agregamos una segunda copia; retiramos todos los nodos SEO gestionados
   * por IntegraNube y dejamos una única versión cliente activa.
   */
  removeManagedSeoNodes();

  document.title = state.title;

  /* Limpiar metadatos estáticos que pueda haber agregado App.js/CRA. */
  document
    .querySelectorAll(
      'meta[name="description"], meta[name="robots"], link[rel="canonical"]'
    )
    .forEach((node) => node.remove());

  addMeta({
    name: 'description',
    content: state.description,
  });

  addMeta({
    name: 'robots',
    content: state.robots,
  });

  addLink({
    rel: 'canonical',
    href: state.canonical,
  });

  if (state.favicon) {
    document
      .querySelectorAll('link[rel*="icon"]')
      .forEach((node) => node.remove());

    addLink({
      rel: 'icon',
      href: state.favicon,
    });
  }

  addMeta({
    property: 'og:title',
    content: state.title,
  });

  addMeta({
    property: 'og:description',
    content: state.description,
  });

  addMeta({
    property: 'og:type',
    content: state.type || 'website',
  });

  addMeta({
    property: 'og:url',
    content: state.canonical,
  });

  if (state.image) {
    addMeta({
      property: 'og:image',
      content: state.image,
    });
  }

  addMeta({
    name: 'twitter:card',
    content: state.image
      ? 'summary_large_image'
      : 'summary',
  });

  addMeta({
    name: 'twitter:title',
    content: state.title,
  });

  addMeta({
    name: 'twitter:description',
    content: state.description,
  });

  if (state.image) {
    addMeta({
      name: 'twitter:image',
      content: state.image,
    });
  }

  if (
    state.route === 'product' &&
    Number.isFinite(Number(state.price)) &&
    Number(state.price) > 0
  ) {
    addMeta({
      property: 'product:price:amount',
      content: state.price,
    });

    addMeta({
      property: 'product:price:currency',
      content: state.currency || 'CLP',
    });
  }

  (state.jsonLd || []).forEach(addJsonLd);
}

function readServerState(pathname, search) {
  if (typeof document === 'undefined') {
    return null;
  }

  const node = document.getElementById('integranube-seo-state');
  if (!node) return null;

  try {
    const state = JSON.parse(node.textContent || '{}');
    const expected = `${pathname}${search || ''}`;

    if (
      state?.path === expected ||
      state?.path === pathname
    ) {
      return state;
    }
  } catch (_) {
    // Ignorar estado inválido.
  }

  return null;
}

/* ===================================================================== */
/* Componente                                                            */
/* ===================================================================== */

export default function SeoClient({
  tienda,
  storeMeta,
}) {
  const location = useLocation();
  const cacheRef = useRef(new Map());

  useEffect(() => {
    let cancelled = false;

    const pathname = location.pathname;
    const search = location.search;

    /*
     * En carga directa usamos exactamente el estado generado por el SSR.
     * applyState() reemplaza los nodos SSR por una sola copia cliente,
     * evitando Product/brand/offers duplicados en el DOM renderizado.
     */
    const serverState = readServerState(pathname, search);

    if (serverState) {
      applyState(serverState);

      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      if (!tienda) return;

      const productIdentifier = canonicalProductIdentifier(pathname);

      if (!productIdentifier) {
        const state = buildState({
          pathname,
          search,
          tienda,
          storeMeta,
          product: null,
        });

        if (state && !cancelled) {
          applyState(state);
        }

        return;
      }

      let product = cacheRef.current.get(productIdentifier);

      if (!product) {
        try {
          const response = await api.get(
            `/productos/${encodeURIComponent(productIdentifier)}`,
            {
              timeout: 5000,
              retries: 2,
            }
          );

          product = response?.data?.data || null;

          if (product) {
            cacheRef.current.set(productIdentifier, product);

            if (product?.slug) {
              cacheRef.current.set(String(product.slug), product);
            }
          }
        } catch (error) {
          console.warn(
            '[SeoClient] No fue posible cargar SEO del producto:',
            error?.response?.status ||
            error?.message ||
            error
          );

          return;
        }
      }

      if (cancelled || !product) {
        return;
      }

      const state = buildState({
        pathname,
        search,
        tienda,
        storeMeta,
        product,
      });

      if (state && !cancelled) {
        applyState(state);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    location.search,
    tienda,
    storeMeta,
  ]);

  return null;
}
